import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, Check, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageIntro, PhotoBanner, Reveal, Img, Container, SectionTitle, SpecList } from "@/components/common/Primitives";
import { api, errorCode } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";
import { SIZES } from "@/lib/images";

const EVENT_IMAGES = {
  celebration: "/images/sunset-terrace.jpg",
  memorial: "/images/view-bay.jpg",
  conference: "/images/hotel-golden-hour.jpg",
};

const CTA_LABEL = {
  celebration: ["Spør om selskap", "Ask about a celebration"],
  memorial: ["Spør om minnesamvær", "Ask about a memorial gathering"],
  conference: ["Spør om møte", "Ask about a meeting"],
};

const Field = ({ id, label, hint, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label htmlFor={id} className="text-sm font-medium text-foreground">{label}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const inputCls = "h-12 rounded-xl bg-card";

export default function Events() {
  const [sp] = useSearchParams();
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  usePageMeta(tx("Selskap og møte", "Events & meetings"), tx("Selskap, minnesamvær, kurs og konferanse ved Fitjarvika, mellom Bergen og Haugesund.", "Celebrations, memorial gatherings, courses and conferences on Fitjarvika, between Bergen and Haugesund."));
  const types = useMemo(() => data?.event_types || [], [data]);
  const catering = data?.dining?.catering || [];
  const [form, setForm] = useState({ event_type: sp.get("type") || "", name: "", email: "", phone: "", company: "", date: "", guests: "", serving: "", message: "", website: "" });
  const [done, setDone] = useState(false);
  useEffect(() => { if (sp.get("type")) setForm((f) => ({ ...f, event_type: sp.get("type") })); }, [sp]);
  useEffect(() => {
    if (window.location.hash === "#forespurnad") setTimeout(() => document.getElementById("forespurnad")?.scrollIntoView({ behavior: "smooth" }), 300);
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const send = useMutation({
    mutationFn: async () => (await api.post("/inquiries", { kind: "event", ...form, guests: form.guests ? parseInt(form.guests, 10) : null, lang })).data,
    onSuccess: () => { setDone(true); toast.success(tx("Takk! Me tek kontakt innan ein arbeidsdag.", "Thank you! We'll be in touch within one working day.")); },
    onError: (err) => toast.error(errorText(errorCode(err), lang)),
  });
  const pick = (id) => { set("event_type", id); document.getElementById("forespurnad")?.scrollIntoView({ behavior: "smooth" }); };

  const steps = [
    { nn: ["Fortel oss om planane", "Send dato, om lag kor mange de blir, og kva de ønskjer hjelp til. Førespurnaden er uforpliktande."], en: ["Tell us about your plans", "Send the date, roughly how many you will be and what you would like help with. The enquiry is non-binding."] },
    { nn: ["Me tek kontakt", "Me svarar vanlegvis innan ein arbeidsdag og går gjennom det de treng."], en: ["We get in touch", "We usually reply within one working day and go through what you need."] },
    { nn: ["Sjå lokala og avtal meny", "De er velkomne innom for å sjå lokala. Me lagar eit menyforslag som passar til selskapet og budsjettet."], en: ["See the venue and agree on a menu", "You are welcome to come and see the venue. We draft a menu proposal that fits the party and the budget."] },
    { nn: ["De får ei skriftleg stadfesting", "Når detaljane er avtalte, sender me ei samla stadfesting."], en: ["You receive written confirmation", "Once the details are agreed, we send one combined confirmation."] },
  ];

  const facts = [
    { label: tx("Selskapslokale", "Function rooms"), value: tx("Lokale med utsikt over fjorden", "Rooms with a view of the fjord") },
    { label: tx("Møtepakke", "Meeting package"), value: tx("Møterom, lunsj og kaffi", "Meeting room, lunch and coffee") },
    { label: tx("Overnatting", "Accommodation"), value: tx("61 rom i tre hus", "61 rooms in three houses") },
    { label: tx("Servering", "Catering"), value: tx("Meny etter avtale", "Menu by arrangement") },
    { label: tx("Reiseveg", "Getting here"), value: tx("Om lag 7 minutt med bil frå Sandvikvåg ferjekai", "About 7 minutes by car from Sandvikvåg ferry quay") },
    { label: tx("Parkering", "Parking"), value: tx("Gratis ved hotellet", "Free at the hotel") },
  ];

  return (
    <div data-testid="events-page">
      <PageIntro
        testid="events-hero"
        title={tx("Selskap og møte ved Fitjarvika", "Events and meetings on Fitjarvika")}
        lead={tx("Fortel oss kva de planlegg, og kor mange de blir. Me hjelper dykk med lokale og servering, og har rom til gjester som vil overnatte.", "Tell us what you are planning and how many you will be. We help with the venue and catering, and have rooms for guests who want to stay over.")}
      >
        <Button size="lg" className="mt-7" onClick={() => pick(form.event_type)} data-testid="events-hero-cta">{tx("Send førespurnad", "Send an enquiry")} <ArrowRight className="h-4 w-4" /></Button>
      </PageIntro>
      <PhotoBanner src="/images/view-bay-wide.jpg" alt={tx("Fitjarvika sett frå hotellet", "Fitjarvika seen from the hotel")} position="50% 60%" testid="events-banner" className="mt-10 lg:mt-12" />

      {/* Types */}
      <section className="py-20 lg:py-28" data-testid="events-types">
        <Container>
          <div className="grid gap-x-7 gap-y-12 md:grid-cols-3">
            {types.map((e, i) => {
              const [label, labelEn] = CTA_LABEL[e.id] || ["Send førespurnad", "Send an enquiry"];
              return (
                <Reveal key={e.id} delay={i * 0.05}>
                  <article className="group flex h-full flex-col" data-testid={`events-type-${e.id}`}>
                    <Img src={EVENT_IMAGES[e.id] || e.image} alt={tr(e.title)} className="img-zoom aspect-[4/3]" radius="rounded-[22px]" sizes={SIZES.third} />
                    <h3 className="mt-5 text-[1.5rem] leading-tight tracking-[-0.02em] text-foreground">{tr(e.title)}</h3>
                    <p className="mt-3 flex-1 text-[0.9375rem] leading-relaxed text-muted-foreground">{tr(e.text)}</p>
                    <div className="mt-5">
                      <Button variant="soft" onClick={() => pick(e.id)} data-testid={`events-type-${e.id}-cta`}>{tx(label, labelEn)} <ArrowRight className="h-4 w-4" /></Button>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Facts */}
      <section className="bg-secondary/70 py-20 lg:py-28" data-testid="events-facts">
        <Container className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <SectionTitle size="lg" title={tx("Lokale, mat og overnatting", "Venue, food and accommodation")} sub={tx("Menyen kan vere alt frå snittar og betasuppe til ein middag med fleire rettar. Me lagar eit forslag ut frå talet på gjester og budsjettet dykkar.", "The menu can be anything from canapés and soup to a dinner with several courses. We make a proposal based on the number of guests and your budget.")} />
            <Button asChild variant="soft" size="lg" className="mt-7" data-testid="events-directions-link"><Link to="/praktisk#veg"><MapPin className="h-4 w-4" /> {tx("Finn vegen til hotellet", "Find your way to the hotel")}</Link></Button>
          </Reveal>
          <Reveal delay={0.06} className="lg:col-span-7">
            <div className="rounded-[24px] bg-card px-6 ring-hairline sm:px-8"><SpecList rows={facts} testid="events-facts-list" /></div>
          </Reveal>
        </Container>
      </section>

      {/* Steps */}
      <section className="py-20 lg:py-28" data-testid="events-steps-section">
        <Container>
          <SectionTitle size="lg" title={tx("Slik planlegg me saman", "How we plan together")} />
          <ol className="mt-10 grid gap-x-7 gap-y-10 sm:grid-cols-2 lg:grid-cols-4" data-testid="events-steps">
            {steps.map((s, i) => {
              const [t, body] = lang === "en" ? s.en : s.nn;
              return (
                <Reveal as="li" key={t} delay={i * 0.05} className="border-t border-foreground pt-5">
                  <p className="text-sm font-semibold tabular text-muted-foreground">0{i + 1}</p>
                  <p className="mt-2 text-[1.0625rem] font-semibold leading-snug text-foreground">{t}</p>
                  <p className="mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">{body}</p>
                </Reveal>
              );
            })}
          </ol>
        </Container>
      </section>

      {/* Enquiry */}
      <section id="forespurnad" className="scroll-mt-24 pb-20 lg:pb-28" data-testid="events-inquiry">
        <Container className="grid gap-12 border-t border-border pt-14 lg:grid-cols-12 lg:gap-16 lg:pt-20">
          <div className="lg:col-span-5">
            <h2 className="text-[2rem] leading-[1.02] tracking-[-0.03em] text-foreground sm:text-[2.5rem]">{tx("Fortel oss kva de planlegg", "Tell us what you are planning")}</h2>
            <p className="mt-4 max-w-[48ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tx("De treng ikkje ha alt klart. Skriv det de veit om dato, gjester og servering. Me tek kontakt for å avtale resten.", "You don't need to have everything ready. Write what you know about the date, guests and catering. We will get in touch to agree on the rest.")}</p>
            <div className="mt-8 hidden lg:block">
              <Img src="/images/hotel-fjord.jpg" alt={tx("Fjordhotellet ved sjøen", "The Fjord Hotel by the sea")} className="aspect-[4/3]" radius="rounded-[22px]" sizes={SIZES.half} />
            </div>
          </div>
          <div className="lg:col-span-7">
            {done ? (
              <div className="rounded-[24px] bg-secondary p-8" data-testid="events-inquiry-success">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground"><Check className="h-5 w-5" /></span>
                <h3 className="mt-6 text-[1.75rem] leading-tight tracking-[-0.02em] text-foreground">{tx("Takk – førespurnaden er sendt.", "Thank you – your enquiry has been sent.")}</h3>
                <p className="mt-3 max-w-[48ch] text-muted-foreground">{tx("Me tek kontakt på e-post eller telefon innan ein arbeidsdag.", "We'll be in touch by email or phone within one working day.")}</p>
              </div>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); send.mutate(); }} className="grid gap-5 sm:grid-cols-2" data-testid="events-inquiry-form">
                <Field id="inquiry-event-type" label={tx("Kva skal de arrangere?", "What are you arranging?")} className="sm:col-span-2">
                  <Select value={form.event_type || "other"} onValueChange={(v) => set("event_type", v === "other" ? "" : v)}>
                    <SelectTrigger id="inquiry-event-type" className={inputCls} data-testid="inquiry-event-type"><SelectValue placeholder={tx("Vel", "Choose")} /></SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {types.map((t) => <SelectItem key={t.id} value={t.id}>{tr(t.title)}</SelectItem>)}
                      <SelectItem value="other">{tx("Anna", "Other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field id="inq-name" label={tx("Namn", "Name")}><Input id="inq-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} autoComplete="name" data-testid="inquiry-name" /></Field>
                <Field id="inq-company" label={tx("Firma eller organisasjon (valfritt)", "Company or organisation (optional)")}><Input id="inq-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} autoComplete="organization" data-testid="inquiry-company" /></Field>
                <Field id="inq-email" label={tx("E-post", "Email")}><Input id="inq-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} autoComplete="email" data-testid="inquiry-email" /></Field>
                <Field id="inq-phone" label={tx("Telefon", "Phone")}><Input id="inq-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} autoComplete="tel" data-testid="inquiry-phone" /></Field>
                <Field id="inq-date" label={tx("Ønskt dato", "Preferred date")}><Input id="inq-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="inquiry-date" /></Field>
                <Field id="inq-guests" label={tx("Om lag kor mange gjester?", "Roughly how many guests?")}><Input id="inq-guests" type="number" min="1" inputMode="numeric" value={form.guests} onChange={(e) => set("guests", e.target.value)} className={inputCls} data-testid="inquiry-guests" /></Field>
                <Field id="inquiry-serving" label={tx("Kva ønskjer de å servere?", "What would you like to serve?")} className="sm:col-span-2">
                  <Select value={form.serving || "undecided"} onValueChange={(v) => set("serving", v === "undecided" ? "" : v)}>
                    <SelectTrigger id="inquiry-serving" className={inputCls} data-testid="inquiry-serving"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="undecided">{tx("Me ønskjer forslag", "We would like a suggestion")}</SelectItem>
                      {catering.map((c) => <SelectItem key={c.id} value={tr(c.title)}>{tr(c.title)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field id="inq-message" label={tx("Er det noko meir me bør vite?", "Anything else we should know?")} className="sm:col-span-2">
                  <Textarea id="inq-message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value.slice(0, 1400))} className="rounded-xl bg-card" placeholder={tx("Fortel gjerne om lokale, servering eller overnatting de har behov for.", "Tell us about the venue, catering or accommodation you need.")} data-testid="inquiry-message" />
                </Field>
                <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div className="flex flex-col gap-4 border-t border-border pt-6 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-muted-foreground">{tx("Me brukar opplysningane til å følgje opp førespurnaden.", "We use your details to follow up on the enquiry.")}</p>
                  <Button type="submit" size="lg" disabled={send.isPending} data-testid="inquiry-submit">{send.isPending ? tx("Sender …", "Sending …") : tx("Send førespurnad", "Send enquiry")} <ArrowRight className="h-4 w-4" /></Button>
                </div>
              </form>
            )}
          </div>
        </Container>
      </section>
    </div>
  );
}
